/**
 * CreditPurchaseModal Component
 * Modal for purchasing credit packages
 * Handles purchase flow and redirects to Stripe checkout
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { CREDIT_PACKAGES } from '../../types/credits';
import { useCreditsContext } from '../../contexts/CreditsContext';
import './CreditPurchaseModal.css';

/**
 * Props for CreditPurchaseModal
 */
interface CreditPurchaseModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
}

/**
 * CreditPurchaseModal component
 * Displays credit packages and handles purchase flow
 * Redirects to Stripe checkout on purchase
 * 
 * @param props - Component props
 * @returns Credit purchase modal component
 */
export function CreditPurchaseModal({ isOpen, onClose }: CreditPurchaseModalProps) {
  const { purchaseCredits } = useCreditsContext();
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  /**
   * Handle purchase button click
   */
  const handlePurchase = async (packageId: string) => {
    try {
      setPurchasingPackage(packageId);
      await purchaseCredits(packageId);
      // Note: purchaseCredits will redirect to Stripe, so code below won't execute
    } catch (error) {
      console.error('Purchase error:', error);
      alert(`Purchase failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setPurchasingPackage(null);
    }
  };

  /**
   * Handle modal backdrop click
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Handle escape key press
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="credit-purchase-modal-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-labelledby="credit-modal-title"
      aria-modal="true"
    >
      <div style={{
        background: 'var(--color-surface)',
        border: '4px solid var(--color-accent)',
        color: 'var(--color-fg)',
        fontFamily: 'var(--font-family)',
        padding: '1.5rem',
        borderRadius: '10px',
        width: '90%',
        maxWidth: '32rem',
        position: 'relative',
        boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)',
        margin: '2rem auto',
        maxHeight: 'calc(100vh - 4rem)'
      }}>
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close modal"
          style={{
            position: 'absolute',
            top: '0.75rem',
            right: '1rem',
            color: 'var(--color-fg)',
            fontSize: '1.25rem',
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          &times;
        </button>

        {/* Title */}
        <h2 
          id="credit-modal-title" 
          style={{
            fontSize: 'var(--font-size-base)',
            letterSpacing: '0.05em',
            marginBottom: '1rem',
            textAlign: 'center'
          }}
        >
          💳 Purchase Credits
        </h2>

        {/* Credit packages */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', overflowY: 'auto', maxHeight: 'calc(100vh - 12rem)' }}>
          {CREDIT_PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              style={{
                border: pkg.popular ? '2px solid var(--color-accent)' : '2px solid var(--color-cell-border)',
                padding: '0.75rem',
                background: pkg.popular ? 'rgba(var(--color-accent-rgb, 15, 255, 15), 0.1)' : 'transparent',
                borderRadius: '4px'
              }}
            >
              {pkg.popular && (
                <div style={{
                  color: 'var(--color-accent)',
                  fontSize: 'var(--font-size-xs)',
                  marginBottom: '0.25rem',
                  fontWeight: 'bold'
                }}>
                  ⭐ MOST POPULAR
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'bold' }}>
                    {pkg.name}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', opacity: '0.75' }}>
                    {pkg.credits} credits
                  </div>
                  {pkg.savings && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)' }}>
                      Save {pkg.savings}!
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasingPackage !== null}
                  aria-label={`Purchase ${pkg.name} for ${pkg.priceDisplay}`}
                  style={{
                    background: 'var(--color-accent)',
                    color: 'var(--color-bg)',
                    padding: '0.25rem 0.75rem',
                    fontSize: 'var(--font-size-xs)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: purchasingPackage !== null ? 'not-allowed' : 'pointer',
                    opacity: purchasingPackage !== null ? '0.5' : '1',
                    transition: 'background-color 0.2s',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  {purchasingPackage === pkg.id ? 'Processing...' : pkg.priceDisplay}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          fontSize: 'var(--font-size-xs)',
          marginTop: '1rem',
          textAlign: 'center',
          opacity: '0.75'
        }}>
          🔒 Secure payment powered by Stripe
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
